import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Database } from "lucide-react";
import { api } from "@/lib/api";

const DOT = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

const TEXT = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red: "text-red-700",
};

const fmtIST = (iso) => {
  if (!iso) return null;
  try {
    return `${new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      timeZone: "Asia/Kolkata",
    })} IST`;
  } catch {
    return null;
  }
};

/** M15: compact backup status light for the Admin dashboard.
 * Links to Admin Settings → System & Backups. Renders nothing while loading
 * or if the status API is unavailable (never blocks the dashboard). */
export const BackupHealthWidget = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/backups/status").then((res) => setStatus(res.data)).catch(() => {});
  }, []);

  if (!status?.health) return null;
  const { level, label } = status.health;
  const lastAt = fmtIST(status.last_backup?.at);

  return (
    <Link
      to="/admin/settings?tab=system"
      data-testid="backup-health-widget"
      title={status.health.detail}
      className="group inline-flex items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 py-2 transition-colors hover:border-primary/40"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {level !== "green" && (
          <span className={`absolute inline-flex h-full w-full rounded-full ${DOT[level]} opacity-60 animate-ping`} />
        )}
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${DOT[level] || "bg-slate-400"}`} data-testid="backup-health-dot" />
      </span>
      <Database className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-left leading-tight">
        <span className={`block text-xs font-semibold ${TEXT[level] || ""}`} data-testid="backup-health-label">{label}</span>
        {lastAt && <span className="block text-[10px] text-muted-foreground">Last backup {lastAt}</span>}
      </span>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
    </Link>
  );
};
